// Cobweb graphic render helper extracted 1:1 from RoomCanvas.tsx. Pure PIXI, no React state.
import * as PIXI from 'pixi.js';
import { smoothWhenMinified } from './textureFilter';
import { TILE_WIDTH, TILE_HEIGHT } from '../../../domain/windowSnap';
import type { CobwebItem } from '../cobwebLayout';

export type CobwebSprite = { outline: PIXI.Container; graphic: PIXI.Container; posKey: string };

export const makeCobwebGraphic = (size: number, color: number = 0xFFFFFF, alpha: number = 0.72, lw: number = 0.8): PIXI.Graphics => {
  const g = new PIXI.Graphics();
  const radius = size / 2;
  const strands = 8;
  const rings = 4;
  for (let s = 0; s < strands; s++) {
    const angle = (s / strands) * Math.PI * 2;
    g.moveTo(0, 0);
    g.lineTo(Math.cos(angle) * radius, Math.sin(angle) * radius);
    g.stroke({ width: lw, color, alpha });
  }
  for (let r = 1; r <= rings; r++) {
    const rr = (r / rings) * radius;
    const pts: number[] = [];
    for (let s = 0; s <= strands; s++) {
      const angle = (s / strands) * Math.PI * 2;
      pts.push(Math.cos(angle) * rr, Math.sin(angle) * rr);
    }
    g.poly(pts, true);
    g.stroke({ width: lw, color, alpha });
  }
  return g;
};

// Footprint debug overlay: one diamond per cobweb footprint tile.
// Extracted 1:1 from RoomCanvas.tsx (cobweb render effect). Pure PIXI, no React state.
// Structural subset of RoomCanvas' CobwebItem — only the fields this builder reads.
export type CobwebFpItem =
  | { id: number; kind: 'floor'; tileX: number; tileY: number }
  | { id: number; kind: 'surface'; surfaceTileX: number; surfaceTileY: number };

export const buildCobwebFpDebugGraphics = (
  cobwebs: readonly CobwebFpItem[],
  halfTW: number,
  halfTH: number,
  visible: boolean,
): PIXI.Graphics => {
  const fpG = new PIXI.Graphics();
  fpG.eventMode = 'none';
  fpG.visible = visible;
  for (const web of cobwebs) {
    let tx: number, ty: number;
    if (web.kind === 'floor') {
      tx = web.tileX;
      ty = web.tileY;
    } else {
      tx = web.surfaceTileX;
      ty = web.surfaceTileY;
    }
    const cx = (tx - ty) * halfTW;
    const cy = (tx + ty + 1) * halfTH;
    fpG.poly([cx, cy - halfTH, cx + halfTW, cy, cx, cy + halfTH, cx - halfTW, cy, cx, cy - halfTH], false);
    if (web.kind === 'floor') {
      fpG.fill({ color: 0xffa500, alpha: 0.3 });
      fpG.stroke({ width: 1, color: 0x8a4f00, alpha: 0.6 });
    } else {
      fpG.fill({ color: 0x8a2be2, alpha: 0.35 });
      fpG.stroke({ width: 1, color: 0x5a1b92, alpha: 0.7 });
    }
  }
  return fpG;
};

// Destroy a cobweb sprite pair. Extracted 1:1 from RoomCanvas.tsx.
export const destroyCobwebSprite = (sprites: { outline: PIXI.Container; graphic: PIXI.Container }): void => {
  for (const node of [sprites.outline, sprites.graphic]) {
    node.parent?.removeChild(node);
    if (node instanceof PIXI.Sprite) {
      const tex = node.texture;
      node.destroy();
      if (tex && !tex.destroyed) tex.destroy(true); // free RenderTexture GPU memory
    } else {
      node.destroy();
    }
  }
};

// Rasterize cobweb graphics to RenderTexture sprites. Extracted 1:1 from RoomCanvas.tsx.
// PIXI.Graphics with thin lines (0.8–1.8px) flicker during viewport pan/zoom
// due to sub-pixel rendering. Converting to a texture with mipmaps (same fix
// used for furniture/house sprites) eliminates this artifact entirely.
export const createCobwebSprites = (
  renderer: PIXI.Renderer | undefined,
  size: number,
  screenX: number,
  screenY: number,
  container: PIXI.Container,
): { outline: PIXI.Container; g: PIXI.Container } => {
  const radius = size / 2;
  const pad = Math.ceil(radius) + 4;
  // Symmetric frame: cobweb is drawn centered at (0,0), capture ±pad in both axes.
  // Anchor 0.5 = center of texture = cobweb center. (Old code had texSize=size+2*pad
  // which was asymmetric and caused the visual center to drift from the tile position.)
  const texSize = 2 * pad;
  const frame = new PIXI.Rectangle(-pad, -pad, texSize, texSize);
  const anchorVal = 0.5;

  let outline: PIXI.Container;
  let g: PIXI.Container;

  if (renderer) {
    // Outline (dark shadow) → RenderTexture sprite
    const outlineG = makeCobwebGraphic(size, 0x000000, 0.22, 1.8);
    const outlineTex = renderer.generateTexture({ target: outlineG, frame });
    outlineG.destroy();
    smoothWhenMinified(outlineTex.source);
    const outlineSprite = new PIXI.Sprite(outlineTex);
    outlineSprite.anchor.set(anchorVal, anchorVal);
    outlineSprite.eventMode = 'none';
    outlineSprite.position.set(screenX, screenY);
    container.addChild(outlineSprite);
    outline = outlineSprite;

    // Fill (white web) → RenderTexture sprite
    const fillG = makeCobwebGraphic(size);
    const fillTex = renderer.generateTexture({ target: fillG, frame });
    fillG.destroy();
    smoothWhenMinified(fillTex.source);
    const fillSprite = new PIXI.Sprite(fillTex);
    fillSprite.anchor.set(anchorVal, anchorVal);
    fillSprite.position.set(screenX, screenY);
    g = fillSprite;
  } else {
    // Fallback: raw PIXI.Graphics (may flicker slightly on old devices without WebGL)
    const outlineG = makeCobwebGraphic(size, 0x000000, 0.22, 1.8);
    outlineG.position.set(screenX, screenY);
    outlineG.eventMode = 'none';
    container.addChild(outlineG);
    outline = outlineG;
    const fillG = makeCobwebGraphic(size);
    fillG.position.set(screenX, screenY);
    g = fillG;
  }

  return { outline, g };
};

// Incrementally sync cobweb PIXI sprites to the current `cobwebs` layout. Extracted
// 1:1 from RoomCanvas.tsx's cobweb render effect. Mutates `spriteMap` in place and
// returns the (possibly newly created) cobweb container + footprint-debug graphics
// so the caller can persist them. `onCobwebTap` carries the sweep/haptic/delete side
// effects that stay owned by the component.
export function syncCobwebSprites(params: {
  roomContainer: PIXI.Container;
  cobwebContainer: PIXI.Container | null;
  spriteMap: Map<number, CobwebSprite>;
  cobwebs: readonly CobwebItem[];
  showGridMode: boolean;
  prevFpGraphics: PIXI.Graphics | null;
  interiorMarkers: Map<number, PIXI.Container>;
  renderer: PIXI.Renderer | undefined;
  onCobwebTap: (cobwebId: number) => void;
}): { cobwebContainer: PIXI.Container; fpGraphics: PIXI.Graphics | null } {
  const { roomContainer, spriteMap, cobwebs, showGridMode, interiorMarkers, renderer, onCobwebTap } = params;

  let cobwebContainer = params.cobwebContainer;
  if (!cobwebContainer) {
    const cc = new PIXI.Container();
    cc.zIndex = 10000; // Above furniture, below admin overlays (999999+)
    roomContainer.addChild(cc);
    cobwebContainer = cc;
    spriteMap.clear();
  }

  const halfTW = TILE_WIDTH / 2;
  const halfTH = TILE_HEIGHT / 2;

  // ── Footprint overlay (debug): always rebuild, it's lightweight ──────────
  let fpGraphics = params.prevFpGraphics;
  if (fpGraphics) {
    fpGraphics.parent?.removeChild(fpGraphics);
    fpGraphics.destroy();
    fpGraphics = null;
  }
  if (cobwebs.length > 0) {
    const fpG = buildCobwebFpDebugGraphics(cobwebs, halfTW, halfTH, showGridMode);
    fpGraphics = fpG;
    // Add fpG at index 0 so it renders behind all cobweb sprites
    cobwebContainer.addChildAt(fpG, 0);
  }

  // ── Remove sprites for cobwebs no longer in the list ────────────────────
  const currentIds = new Set(cobwebs.map(w => w.id));
  for (const [id, sprites] of spriteMap.entries()) {
    if (!currentIds.has(id)) {
      destroyCobwebSprite(sprites);
      spriteMap.delete(id);
    }
  }

  // ── Add sprites for cobwebs not yet rendered (or whose position changed) ─────
  for (const web of cobwebs) {
    // Compute a position key to detect if a cobweb moved since last render.
    // This happens on the first entry when furniture data loads after isSceneReady.
    const posKey = web.kind === 'floor'
      ? `f:${web.tileX},${web.tileY}`
      : `s:${web.furnitureId}:${web.surfaceTileX},${web.surfaceTileY}`;
    const existing = spriteMap.get(web.id);
    if (existing) {
      if (existing.posKey === posKey) continue; // same position — nothing to do
      // Position changed: destroy stale sprite so it gets recreated below
      destroyCobwebSprite(existing);
      spriteMap.delete(web.id);
    }

    let screenX: number;
    let screenY: number;

    if (web.kind === 'floor') {
      screenX = (web.tileX - web.tileY) * halfTW;
      // Place cobweb so its BOTTOM EDGE aligns with the footprint tile center.
      // Footprint center = (tileX+tileY+1)*halfTH. Cobweb center is (size/2) px above that.
      screenY = (web.tileX + web.tileY + 1) * halfTH - web.size / 2;
    } else {
      screenX = (web.surfaceTileX - web.surfaceTileY) * halfTW;
      const ownerMarker = interiorMarkers.get(web.furnitureId);
      const ownerSprite = ownerMarker?.children[0] as PIXI.Sprite | undefined;
      if (ownerMarker && ownerSprite) {
        screenY = ownerMarker.y - Math.abs(ownerSprite.height) * 0.85;
      } else {
        screenY = (web.surfaceTileX + web.surfaceTileY) * halfTH - 35;
      }
    }

    // Rasterize cobweb graphics to RenderTexture sprites (fixes thin-line flicker
    // during viewport pan/zoom via mipmapped textures).
    const { outline, g } = createCobwebSprites(renderer, web.size, screenX, screenY, cobwebContainer);

    g.alpha = 0.85;
    g.eventMode = 'static';
    g.cursor = 'pointer';
    g.hitArea = new PIXI.Circle(0, 0, web.size / 2);

    // Simple tap removes the cobweb immediately (no radial menu needed).
    g.on('pointertap', () => onCobwebTap(web.id));

    cobwebContainer.addChild(g);
    spriteMap.set(web.id, { outline, graphic: g, posKey });
  }

  return { cobwebContainer, fpGraphics };
}
